import { showDialog, PlanLearnMoreDialog } from '@zoom/fe-billing-components';

import pcm from '@/global-install/global-pcm';
import { translation } from '@/plugins/i18n';
import { planUnitMap } from '@/common/product-unit';
import { 
  DEFAULT_MODAL_WIDTH,
  DEFAULT_MODAL_HEIGHT,
  jumpToTargetPageForDiffType,
  resizeModalDialogIniFrame,
  hideModalDialogCloseButton,
  showModalDialogCloseButton,
} from '@/utils/modal-utils';
import { OPC_REDIRECT_QUERY_PARAMS } from '@/common/constants';
import EventTracking from '@/aspects/trackings';

export const PLAN_SELECTION_ID = {
  SCHEDULER_BASIC: 'scheduler-basic',
  SCHEDULER_PLAN: 'scheduler-plan',
  WORKPLACE_PRO: 'workplace-pro',
  WORKPLACE_BUSINESS: 'workplace-business',
  WORKPLACE_BUSINESS_PLUS: 'workplace-business-plus',
  WORKPLACE_PRO_PLUS: 'workplace-pro-plus',
  CLIPS_BASIC: 'clips-basic',
};

export const PLAN_SELECTION_TRACKING_MAP = {
  [PLAN_SELECTION_ID.SCHEDULER_BASIC]: 'scheduler',
  [PLAN_SELECTION_ID.SCHEDULER_PLAN]: 'scheduler',
  [PLAN_SELECTION_ID.WORKPLACE_PRO]: 'workplace pro',
  [PLAN_SELECTION_ID.WORKPLACE_BUSINESS]: 'workplace business',
  [PLAN_SELECTION_ID.WORKPLACE_BUSINESS_PLUS]: 'workplace business plus',
  [PLAN_SELECTION_ID.WORKPLACE_PRO_PLUS]: 'workplace pro plus',
  [PLAN_SELECTION_ID.CLIPS_BASIC]: 'clips',
};

const jumpToProductBuyPage = (targetUrl) => {
  resizeModalDialogIniFrame({ width: DEFAULT_MODAL_WIDTH, height: DEFAULT_MODAL_HEIGHT });
  jumpToTargetPageForDiffType(targetUrl, OPC_REDIRECT_QUERY_PARAMS);
};

const handlePlanLearnMoreBuyNow = (params = {}) => {
  const { buy_url: buyUrl } = params?.planConfig || {};
  if (!buyUrl) {
    return;
  }
  jumpToTargetPageForDiffType(buyUrl, OPC_REDIRECT_QUERY_PARAMS);
};

const getCommonBasicFeatures = () => ([
  {
    category: translation('planCard.meetings.meetings'),
    categoryIconName: 'meetings',
    features: [
      { text: translation('planCard.meetings.xMinutesPerMeeting', [40]) },
      { text: translation('planCard.meetings.xParticipantsPerMeeting', [100]) },
    ]
  },
  {
    category: translation('planCard.aiCompanion.aiCompanion'),
    categoryIconName: 'ai-companion-feature-list-icon',
    features: [
      { text: translation('planCard.aiCompanion.limitedInMeetingUse') },
    ]
  },
  {
    category: translation('planCard.teamChat.teamChat'),
    categoryIconName: 'team-chat-feature-list-icon',
    features: [
      { text: translation('planCard.teamChat.instantMessaging') },
    ]
  },
  {
    category: translation('planCard.whiteboard.whiteboard'),
    categoryIconName: 'whiteboard-feature-list-icon',
    features: [
      { text: translation('planCard.whiteboard.xEditableWhiteboards', [3]) },
    ]
  },
  {
    category: translation('planCard.docs.docs'),
    categoryIconName: 'docs-feature-list-icon',
    features: [
      { text: translation('planCard.docs.shareUpToXDocs', [10]) },
    ]
  },
  {
    category: translation('planCard.clips.clips'),
    categoryIconName: 'clips-feature-list-icon',
    features: [
      { text: translation('planCard.clips.xTwoMinuteClips', [5]) },
    ]
  },
  {
    category: translation('planCard.mail.mail'),
    categoryIconName: 'mail-feature-list-icon',
    features: [
      { text: translation('planCard.mail.connectGmailOutlookInZoom') },
    ]
  },
  {
    category: translation('planCard.calendar.calendar'),
    categoryIconName: 'calendar-feature-list-icon',
    features: [
      { text: translation('planCard.calendar.syncGoogleMicrosoftCalendars') },
    ]
  },
  {
    category: translation('planCard.tasks.tasks'),
    categoryIconName: 'tasks-feature-list-icon',
    features: [
      { text: translation('planCard.tasks.surfaceManageAndCompleteTasks') },
    ]
  },
  {
    category: translation('planCard.hub.hub'),
    categoryIconName: 'hub-feature-list-icon',
    features: [
      { text: translation('planCard.hub.onePlaceForYourZoomFiles') },
    ]
  },
])

const getCommonProFeatures = (from = '', planName = '') => ([
  {
    category: translation('planCard.meetings.meetings'),
    categoryIconName: 'meetings',
    features: [
      { 
        text: translation('planCard.meetings.xHoursPerMeeting', [30]), 
      },
      {
        text: translation('planCard.meetings.xParticipantsPerMeeting', [100]),
        link: {
          text: translation('billing.large_meeting.increase_with'),
          callback: () => {
            if (from && planName) {
              EventTracking.buyInteraction(`zoom ${from} - standalone in-product web modal checkout`, `select a product modal - ${planName} - increase with large meeting`);
            }
            hideModalDialogCloseButton();
            showDialog(
              pcm,
              PlanLearnMoreDialog,
              { visible: true, planType: 'large' },
              {
                onClick: (evt, type, params) => {
                  if (type === 'BUY_NOW_BUTTON') {
                    // NOTE: For modal checkout / client checkout, need to save some params to the url
                    handlePlanLearnMoreBuyNow(params);
                  }
                },
                onClose: () => {
                  showModalDialogCloseButton();
                }
              }
            );
          }
        }
      },
    ]
  },
  {
    category: translation('planCard.aiCompanion.aiCompanion'),
    categoryIconName: 'ai-companion-feature-list-icon',
    features: [
      { text: translation('planCard.aiCompanion.unlimitedInMeetingUse') },
      { text: translation('planCard.aiCompanion.inZoomWorkplaceAndOnTheWeb') },
      { link: 
        { 
          text: translation('planCard.aiCompanion.andMore'), 
          callback: () => {
            if (from && planName) {
              EventTracking.buyInteraction(`zoom ${from} - standalone in-product web modal checkout`, `select a product modal - ${planName} - and more`);
            }
            hideModalDialogCloseButton();
            showDialog(
              pcm,
              PlanLearnMoreDialog,
              { visible: true, planType: 'ai' },
              {
                onClose: () => {
                  showModalDialogCloseButton();
                }
              }
            );
          }
        }
      },
    ]
  },
  {
    category: translation('planCard.docs.docs'),
    categoryIconName: 'docs-feature-list-icon',
    features: [
      { text: translation('planCard.docs.unlimitedDocs') },
    ]
  },
  {
    category: translation('planCard.mail.mail'),
    categoryIconName: 'mail-feature-list-icon',
    features: [
      { text: translation('planCard.mail.zoomMail') },
    ]
  },
  {
    category: translation('planCard.calendar.calendar'),
    categoryIconName: 'calendar-feature-list-icon',
    features: [
      { text: translation('planCard.calendar.zoomCalendar') },
    ]
  },
  {
    category: translation('planCard.cloudStorage.cloudStorage'),
    categoryIconName: 'cloud-storage-feature-list-icon',
    features: [
      { text: translation('planCard.cloudStorage.xGB', [10]) },
    ]
  },
  {
    category: translation('planCard.liveChatSupport.liveChatSupport'),
    categoryIconName: 'live-chat-feature-list-icon',
  },
])

export const getSchedulerPlanSelectionConfigs = (userPlanType = '') => {
  const isFreeUser = userPlanType === 'free';
  const isProUser = userPlanType === 'pro';
  const isProPlusUser = userPlanType === 'pro_plus';

  if (!userPlanType || userPlanType === 'other') {
    return [];
  }

  const basicPlan = {
    id: PLAN_SELECTION_ID.SCHEDULER_BASIC,
    iconName: 'scheduler-card',
    name: translation('planCard.scheduler.scheduler'),
    title: translation('common.basic'),
    priceString: translation('common.free'),
    isPopular: false,
    hideBtn: true,
    highlightsText: translation('planCard.highlights'),
    highlights: [
      {
        category: translation('planCard.scheduler.schedulerBasic'),
        categoryIconName: 'scheduler-feature-list-icon',
        collapsedCount: 3,
        features: [
          { text: translation('planCard.scheduler.1personalBookingPage') },
          { text: translation('planCard.scheduler.connect1GoogleMicrosoft365OutlookcomOrIcloudCalendar') },
          { text: translation('planCard.scheduler.meetOnZoomGoogleMeetOrMicrosoftTeams') },
          { text: translation('planCard.scheduler.unlimitedMeetingPollsAndOneOffBookingPages') },
          { text: translation('planCard.scheduler.customizeYourBookingPageLogoAndBookingLink') },
          { text: translation('planCard.scheduler.availableOnMobileApp') },
        ]
      },
      {
        type: 'divide',
      },
      ...getCommonBasicFeatures(),
    ]
  };

  const schedulerPlan = {
    id: PLAN_SELECTION_ID.SCHEDULER_PLAN,
    iconName: 'scheduler-card',
    name: translation('planCard.scheduler.scheduler'),
    title: translation('planCard.scheduler.scheduler'),
    priceKey: 'zsched_yearly',
    priceUnit: planUnitMap.user,
    isPopular: false,
    btnCallback: ({ closeDialog }) => {
      closeDialog();
      resizeModalDialogIniFrame({ width: DEFAULT_MODAL_WIDTH, height: DEFAULT_MODAL_HEIGHT });
    },
    highlightsText: translation('planCard.allOfPlanNameAnd', { planName: translation('planCard.scheduler.schedulerBasic') }),
    highlights: [
      {
        category: translation('planCard.scheduler.scheduler'),
        categoryIconName: 'scheduler-feature-list-icon',
        collapsedCount: 4,
        features: [
          { text: translation('planCard.scheduler.unlimitedBookingPages') },
          { text: translation('planCard.scheduler.scheduleRoundRobinAndGroupMeetings') },
          { text: translation('planCard.scheduler.connectUpTo6Calendars', [6]) },
          { text: translation('planCard.scheduler.automateEmailAndSMSRemindersForAttendees') },
          { text: translation('planCard.scheduler.removeZoomSchedulerBranding') },
          { text: translation('planCard.scheduler.customizeYourBookingPageWithBrandColorsAndLogo') },
          { text: translation('planCard.scheduler.schedule11AndGroupSessions') },
          { text: translation('planCard.scheduler.takePaymentsWithStripeCreditDebitApplePayAndGooglePay') },
          { text: translation('planCard.scheduler.qualifyAndRouteLeadsWithRoutingForms') },
          { text: translation('planCard.scheduler.syncScheduledMeetingsToSalesforce') },
          { text: translation('planCard.scheduler.viewAnalyticsAndInsightsToOptimizeBookings') },
          { text: translation('planCard.scheduler.manageSchedulesAndGroupsAcrossLargerTeams') },
          { text: translation('planCard.scheduler.availableInUSAndUKOnly'), hideCheckIcon: true },
        ]
      }
    ]
  };

  const proPlan = {
    id: PLAN_SELECTION_ID.WORKPLACE_PRO,
    iconName: 'zoom',
    name: translation('billing.Workplace'),
    title: translation('common.pro'),
    priceKey: ['zo_pro_yearly', 'yearly_c50'],
    priceUnit: planUnitMap.user,
    btnCtaLabel: translation('common.upgrade'),
    btnCallback: () => {
      jumpToProductBuyPage('/buy?product=PRO');
    },
    highlightsText: translation('planCard.highlights'),
    highlights: [
      ...getCommonProFeatures('scheduler', 'workplace pro').map(item => {
        if (item.categoryIconName === 'ai-companion-feature-list-icon') {
          return {
            ...item,
            collapsedCount: 1,
          };
        }
        return item;
      }),
    ]
  }

  const bizPlan = {
    id: PLAN_SELECTION_ID.WORKPLACE_BUSINESS,
    iconName: 'zoom',
    name: translation('billing.Workplace'),
    title: translation('common.business'),
    priceKey: 'business_yearly_c50',
    priceUnit: planUnitMap.user,
    isPopular: true,
    highlightsText: translation('planCard.allOfPlanNameAnd', { planName: translation('planCard.scheduler.scheduler') }),
    btnCtaLabel: translation('common.upgrade'),
    btnCallback: () => {
      jumpToProductBuyPage('/buy?product=BIZ');
    },
    highlights: [
      {
        category: translation('planCard.meetings.meetings'),
        categoryIconName: 'meetings',
        features: [
          { text: translation('planCard.meetings.xHoursPerMeeting', [30]) },
          { 
            text: translation('planCard.meetings.xParticipantsPerMeeting2', [300]),
            link: {
              text: translation('billing.large_meeting.increase_with'),
              callback: () => {
                EventTracking.buyInteraction(`zoom scheduler - standalone in-product web modal checkout`, `select a product modal - workplace business - increase with large meeting`);
                hideModalDialogCloseButton();
                showDialog(
                  pcm,
                  PlanLearnMoreDialog,
                  { visible: true, planType: 'large' },
                  {
                    onClick: (evt, type, params) => {
                      if (type === 'BUY_NOW_BUTTON') {
                        // NOTE: For modal checkout / client checkout, need to save some params to the url
                        handlePlanLearnMoreBuyNow(params);
                      }
                    },
                    onClose: () => {
                      showModalDialogCloseButton();
                    }
                  }
                );
              }
            }
          },
        ]
      },
      {
        category: translation('planCard.aiCompanion.aiCompanion'),
        categoryIconName: 'ai-companion-feature-list-icon',
        collapsedCount: 1,
        features: [
          { text: translation('planCard.aiCompanion.unlimitedInMeetingUse') },
          { text: translation('planCard.aiCompanion.inZoomWorkplaceAndOnTheWeb') },
          { link: 
            { 
              text: translation('planCard.aiCompanion.andMore'), 
              callback: () => {
                EventTracking.buyInteraction(`zoom scheduler - standalone in-product web modal checkout`, `select a product modal - workplace business - and more`);
                hideModalDialogCloseButton();
                showDialog(
                  pcm,
                  PlanLearnMoreDialog,
                  { visible: true, planType: 'ai' },
                  {
                    onClose: () => {
                      showModalDialogCloseButton();
                    }
                  }
                );
              }
            }
          },
        ]
      },
      {
        category: translation('planCard.teamChat.teamChat'),
        categoryIconName: 'team-chat-feature-list-icon',
        features: [
          { text: translation('planCard.teamChat.instantMessaging') },
        ]
      },
      {
        category: translation('billing.scheduler.short_title'),
        categoryIconName: 'scheduler-feature-list-icon',
        features: [
          { text: translation('planCard.scheduler.letOthersBookTimeWithYouEasily') },
        ]
      },
      {
        category: translation('planCard.whiteboard.whiteboard'),
        categoryIconName: 'whiteboard-feature-list-icon',
        features: [
          { text: translation('planCard.whiteboard.unlimitedWhiteboards') },
        ]
      },
      {
        category: translation('planCard.docs.docs'),
        categoryIconName: 'docs-feature-list-icon',
        features: [
          { text: translation('planCard.docs.unlimitedDocs') },
        ]
      },
      {
        category: translation('planCard.clips.clips'),
        categoryIconName: 'clips-feature-list-icon',
        features: [
          { text: translation('planCard.clips.unlimitedClips') },
          { text: translation('planCard.clips.customAvatars', [3]) },
        ]
      },
      {
        category: translation('planCard.mail.mail'),
        categoryIconName: 'mail-feature-list-icon',
        features: [
          { text: translation('planCard.mail.zoomMail') },
        ]
      },
      {
        category: translation('planCard.calendar.calendar'),
        categoryIconName: 'calendar-feature-list-icon',
        features: [
          { text: translation('planCard.calendar.zoomCalendar') },
        ]
      },
      {
        category: translation('planCard.tasks.tasks'),
        categoryIconName: 'tasks-feature-list-icon',
        features: [
          { text: translation('planCard.tasks.surfaceManageAndCompleteTasks') },
        ]
      },
      {
        category: translation('planCard.hub.hub'),
        categoryIconName: 'hub-feature-list-icon',
        features: [
          { text: translation('planCard.hub.onePlaceForYourZoomFiles') },
        ]
      },
      {
        category: translation('planCard.cloudStorage.cloudStorage'),
        categoryIconName: 'cloud-storage-feature-list-icon',
        features: [
          { text: translation('planCard.cloudStorage.xGB', [10]) },
        ]
      },
      {
        category: translation('planCard.liveChatSupport.liveChatSupport'),
        categoryIconName: 'live-chat-feature-list-icon',
      },
      {
        category: translation('planCard.extras.extras'),
        categoryIconName: 'check',
        features: [
          { text: translation('planCard.extras.ssoManagedDomains') },
          { text: translation('planCard.extras.multiRegionStorageAndMediaControl') },
          { text: translation('planCard.extras.deviceManagement') },
          { text: translation('planCard.extras.informationBarriersAndOtherPolicies') },
          { text: translation('planCard.extras.archivalAndDataLossPreventionAPIs') },
        ]
      }
    ]
  };

  const proPlusPlan = {
    id: PLAN_SELECTION_ID.WORKPLACE_PRO_PLUS,
    iconName: ['phone', 'zoom'],
    name: translation('common.phoneAndWorkplace'),
    title: translation('billing.zone_pro_plus.brief_title'),
    priceKey: 'zwpp_us_ca_yearly',
    priceUnit: planUnitMap.user,
    btnCtaLabel: translation('common.upgrade'),
    btnCallback: () => {
      jumpToProductBuyPage('/buy?product=ZONEPROPZVU');
    },
    highlightsText: translation('planCard.allOfPlanNameAnd', { planName: translation('planCard.scheduler.scheduler') }),
    highlights: [
      {
        category: translation('planCard.phone.phone'),
        categoryIconName: 'phone-feature-list-icon',
        features: [
          { text: translation('planCard.phone.domesticCallingUnlimited') },
        ]
      },
      {
        type: 'divide',
      },
      ...getCommonProFeatures('scheduler', 'workplace pro plus').map(item => {
        if (item.categoryIconName === 'meetings') {
          return {
            ...item,
            collapsedCount: 2,
          };
        }
        return item;
      }),
    ]
  }

  const bizPlusPlan = {
    id: PLAN_SELECTION_ID.WORKPLACE_BUSINESS_PLUS,
    iconName: ['phone', 'zoom'],
    name: translation('common.phoneAndWorkplace'),
    title: translation('one.bizPlus'),
    priceKey: 'zonebizpzvu_yearly',
    priceUnit: planUnitMap.user,
    isPopular: true,
    btnCtaLabel: translation('common.upgrade'),
    btnCallback: () => {
      jumpToProductBuyPage('/buy?product=ZONEBIZPZVU');
    },
    highlightsText: translation('planCard.allOfPlanNameAnd', { planName: translation('billing.zone_pro_plus.brief_title') }),
    highlights: [
      {
        category: translation('planCard.phone.phone'),
        categoryIconName: 'phone-feature-list-icon',
        features: [
          { text: translation('planCard.phone.domesticOnlineFaxUnlimited') },
        ]
      },
      {
        type: 'divide',
      },
      {
        category: translation('planCard.meetings.meetings'),
        categoryIconName: 'meetings',
        collapsedCount: 1,
        features: [
          { 
            text: translation('planCard.meetings.xParticipantsPerMeeting2', [300]),
            link: {
              text: translation('billing.large_meeting.increase_with'),
              callback: () => {
                EventTracking.buyInteraction(`zoom scheduler - standalone in-product web modal checkout`, `select a product modal - workplace business plus - increase with large meeting`);
                hideModalDialogCloseButton();
                showDialog(
                  pcm,
                  PlanLearnMoreDialog,
                  { visible: true, planType: 'large' },
                  {
                    onClick: (evt, type, params) => {
                      if (type === 'BUY_NOW_BUTTON') {
                        // NOTE: For modal checkout / client checkout, need to save some params to the url
                        handlePlanLearnMoreBuyNow(params);
                      }
                    },
                    onClose: () => {
                      showModalDialogCloseButton();
                    }
                  }
                );
              }
            }
          },
        ]
      },
      {
        category: translation('billing.scheduler.short_title'),
        categoryIconName: 'scheduler-feature-list-icon',
        features: [
          { text: translation('planCard.scheduler.letOthersBookTimeWithYouEasily') },
        ]
      },
      {
        category: translation('planCard.whiteboard.whiteboard'),
        categoryIconName: 'whiteboard-feature-list-icon',
        features: [
          { text: translation('planCard.whiteboard.unlimitedWhiteboards') },
        ]
      },
      {
        category: translation('planCard.extras.extras'),
        categoryIconName: 'check',
        features: [
          { text: translation('planCard.extras.ssoManagedDomains') },
          { text: translation('planCard.extras.multiRegionStorageAndMediaControl') },
          { text: translation('planCard.extras.deviceManagement') },
          { text: translation('planCard.extras.informationBarriersAndOtherPolicies') },
          { text: translation('planCard.extras.archivalAndDataLossPreventionAPIs') },
        ]
      }
    ]
  }

  // return [proPlusPlan, schedulerPlan, bizPlusPlan];

  if (isFreeUser) {
    basicPlan.isCurrent = true;
    return [basicPlan, schedulerPlan, bizPlan];
  }
  if (isProUser) {
    proPlan.hideBtn = true;
    proPlan.isCurrent = true;
    return [proPlan, schedulerPlan, bizPlan];
  }
  if (isProPlusUser) {
    if (schedulerPlan?.highlights?.[0]) {
      schedulerPlan.highlights[0].collapsedCount = 5;
    }
    proPlusPlan.isCurrent = true;
    proPlusPlan.hideBtn = true;
    return [proPlusPlan, schedulerPlan, bizPlusPlan];
  }
};

export const getClipsPlanSelectionConfigs = () => {

  const basicPlan = {
    id: PLAN_SELECTION_ID.CLIPS_BASIC,
    iconName: 'clips-card',
    name: translation('planCard.clips.clips'),
    title: translation('common.basic'),
    priceString: translation('common.free'),
    isPopular: false,
    hideBtn: true,
    highlightsText: translation('planCard.highlights'),
    highlights: [
      {
        category: translation('planCard.clips.clips'),
        categoryIconName: 'clips-feature-list-icon',
        collapsedCount: 5,
        features: [
          { text: translation('planCard.clips.upToXRecordingsPerPerson', [5]) },
          { text: translation('planCard.clips.xMinuteScreenRecordingLimit', [3]) },
          { text: translation('planCard.clips.personalLibraryAndPlaylists') },
          { text: translation('planCard.clips.basicVideoQualityUpTo720p') },
          { text: translation('planCard.clips.screenWebcamRecording') },
          { text: translation('planCard.clips.annotationsWhileRecording') },
          { text: translation('planCard.clips.gifAndCustomVideoThumbnails') },
          { text: translation('planCard.clips.trimVideos') },
          { text: translation('planCard.clips.emojiReactionsAndVideoComments') },
          { text: translation('planCard.clips.xMinuteTextToAvatarGenerationTryOut', [1]) },
        ]
      },
      {
        type: 'divide',
      },
      {
        category: translation('planCard.meetings.meetings'),
        categoryIconName: 'meetings',
        features: [
          { text: translation('planCard.meetings.xMinutesPerMeeting', [40]) },
          { text: translation('planCard.meetings.xParticipantsPerMeeting', [100]) },
        ]
      },
      {
        category: translation('planCard.teamChat.teamChat'),
        categoryIconName: 'team-chat-feature-list-icon',
        features: [
          { text: translation('planCard.teamChat.instantMessaging') },
        ]
      },
      {
        category: translation('planCard.phone.phone'),
        categoryIconName: 'phone-feature-list-icon',
        features: [
          { text: translation('planCard.phone.voipCalling') },
        ]
      },
      {
        category: translation('planCard.whiteboard.whiteboard'),
        categoryIconName: 'whiteboard-feature-list-icon',
        features: [
          { text: translation('planCard.whiteboard.xEditableWhiteboards', [3]) },
        ]
      },
      {
        category: translation('planCard.docs.docs'),
        categoryIconName: 'docs-feature-list-icon',
        features: [
          { text: translation('planCard.docs.shareUpToXDocs', [10]) },
        ]
      },
      {
        category: translation('planCard.mail.mail'),
        categoryIconName: 'mail-feature-list-icon',
        features: [
          { text: translation('planCard.mail.connectGmailOutlookInZoom') },
        ]
      },
      {
        category: translation('planCard.calendar.calendar'),
        categoryIconName: 'calendar-feature-list-icon',
        features: [
          { text: translation('planCard.calendar.syncGoogleMicrosoftCalendars') },
        ]
      },
      {
        category: translation('planCard.tasks.tasks'),
        categoryIconName: 'tasks-feature-list-icon',
        features: [
          { text: translation('planCard.tasks.surfaceManageAndCompleteTasks') },
        ]
      },
      {
        category: translation('planCard.hub.hub'),
        categoryIconName: 'hub-feature-list-icon',
        features: [
          { text: translation('planCard.hub.onePlaceForYourZoomFiles') },
        ]
      },
    ]
  };

  const proPlan = {
    id: PLAN_SELECTION_ID.WORKPLACE_PRO,
    iconName: 'zoom',
    name: translation('billing.Workplace'),
    title: translation('common.pro'),
    priceKey: ['zo_pro_yearly', 'yearly_c50'],
    priceUnit: planUnitMap.user,
    isPopular: true,
    btnCtaLabel: translation('common.upgrade'),
    btnCallback: ({ closeDialog }) => {
      closeDialog();
      resizeModalDialogIniFrame({ width: DEFAULT_MODAL_WIDTH, height: DEFAULT_MODAL_HEIGHT });
    },
    highlightsText: translation('planCard.allOfPlanNameAnd', { planName: translation('planCard.clips.clipsBasic') }),
    highlights: [
      {
        category: translation('planCard.clips.clips'),
        categoryIconName: 'clips-feature-list-icon',
        collapsedCount: 5,
        features: [
          { text: translation('planCard.clips.unlimitedRecordingsAndDuration') },
          { text: translation('planCard.clips.highDefinitionVideoUpTo4K') },
          { text: translation('planCard.clips.stitchMultipleClipsTogether') },
          { text: translation('planCard.clips.uploadExternalVideoFiles') },
          { text: translation('planCard.clips.viewerAndAdminInsights') },
          { text: translation('planCard.clips.customBrandingLogoColorThemes') },
          { text: translation('planCard.clips.zoomAiCompanionTitlesDescriptionsChaptersTags') },
          { text: translation('planCard.clips.xCustomAvatars', [3]) },
          { text: translation('planCard.clips.xMinutesPerMonthOfTextToAvatarGeneration', [3]) },
          { text: translation('planCard.clips.customDataRetentionPolicies') },
        ]
      },
      ...getCommonProFeatures('clips', 'workplace pro'),
    ]
  }

  return [basicPlan, proPlan];
};